import { useState, useEffect, useCallback } from 'react';
import api from '../../../../../services/api';

/**
 * Hook to manage version history state and operations
 */
export const useVersionHistory = (rfpId, activeSection, setEditableData, editableData) => {
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const [versionsList, setVersionsList] = useState([]);
    const [activeVersionMenuId, setActiveVersionMenuId] = useState(null);
    const [isFetchingVersions, setIsFetchingVersions] = useState(false);
    const [isFetchingVersionDetails, setIsFetchingVersionDetails] = useState(false);
    const [isFetchingComparison, setIsFetchingComparison] = useState(false);

    // Additional modals state often associated with versioning
    const [showLabelModal, setShowLabelModal] = useState(false);
    const [labelTargetVersion, setLabelTargetVersion] = useState(null);
    const [versionLabelText, setVersionLabelText] = useState('');

    const [showRevertModal, setShowRevertModal] = useState(false);
    const [revertTargetVersion, setRevertTargetVersion] = useState(null);
    const [revertReason, setRevertReason] = useState('Restoring to previous draft');

    const [showVersionDetailsModal, setShowVersionDetailsModal] = useState(false);
    const [selectedVersionData, setSelectedVersionData] = useState(null);
    const [isSelectingCompareFromView, setIsSelectingCompareFromView] = useState(false);
    const [compareSourceVersion, setCompareSourceVersion] = useState(null);

    const fetchVersions = useCallback(async () => {
        if (!rfpId || !activeSection) return;
        setIsFetchingVersions(true);
        try {
            // Find section object to get its ID
            const sectionObj = editableData?.sections?.find(s => (s.label || s.key) === activeSection);
            const sectionId = sectionObj?.id || sectionObj?._id;

            if (sectionId) {
                const response = await api.get(`/rfp/${rfpId}/sections/${sectionId}/versions`);
                setVersionsList(response.data.versions || []);
            } else {
                // Fallback to legacy label search if ID not found
                const response = await api.get(`/rfp/${rfpId}/versions`, {
                    params: { section: activeSection }
                });
                setVersionsList(response.data.versions || []);
            }
        } catch (error) {
            console.error('Error fetching versions:', error);
        } finally {
            setIsFetchingVersions(false);
        }
    }, [rfpId, activeSection, editableData]);

    useEffect(() => {
        if (rfpId && activeSection && (editableData?.sections || showVersionHistory)) {
            fetchVersions();
        }
    }, [rfpId, activeSection, editableData, showVersionHistory, fetchVersions]);

    const handleViewVersion = useCallback(async (v) => {
        setIsFetchingVersionDetails(true);
        try {
            // Use version_number for the URL as per user request (e.g., .../versions/2)
            const verNum = v.version_number || v.version_id || v.id;

            // Find section object to get its ID
            const sectionObj = editableData?.sections?.find(s => (s.label || s.key) === activeSection);
            const sectionId = sectionObj?.id || sectionObj?._id;

            if (sectionId && verNum) {
                const response = await api.get(`/rfp/${rfpId}/sections/${sectionId}/versions/${verNum}`);
                setSelectedVersionData(response.data);
                setShowVersionDetailsModal(true);
            } else {
                console.error('Missing sectionId or versionNumber:', { sectionId, verNum });
            }
        } catch (error) {
            console.error('Error viewing version:', error);
        } finally {
            setIsFetchingVersionDetails(false);
        }
    }, [rfpId, activeSection, editableData]);

    const handleRenameVersion = useCallback(async (verNum, newLabel) => {
        if (!verNum || !newLabel) return;
        try {
            // Find section object to get its ID
            const sectionObj = editableData?.sections?.find(s => (s.label || s.key) === activeSection);
            const sectionId = sectionObj?.id || sectionObj?._id;

            if (!sectionId) throw new Error("Section ID not found");

            await api.patch(`/rfp/${rfpId}/sections/${sectionId}/versions/${verNum}/label`, {
                label: newLabel
            });
            fetchVersions();
        } catch (error) {
            console.error('Error renaming version:', error);
        }
    }, [rfpId, activeSection, editableData, fetchVersions]);

    const handleRevertVersion = useCallback(async () => {
        if (!revertTargetVersion) return;
        try {
            const verNum = revertTargetVersion.version_number || revertTargetVersion.version_id || revertTargetVersion.id;
            
            // Find section object to get its ID
            const sectionObj = editableData?.sections?.find(s => (s.label || s.key) === activeSection);
            const sectionId = sectionObj?.id || sectionObj?._id;

            if (!sectionId) throw new Error("Section ID not found");

            await api.post(`/rfp/${rfpId}/sections/${sectionId}/versions/revert`, {
                target_version: verNum,
                reason: revertReason
            });
            
            setShowRevertModal(false);
            // Refresh main data after revert
            window.location.reload();
        } catch (error) {
            console.error('Error reverting version:', error);
        }
    }, [rfpId, activeSection, editableData, revertTargetVersion, revertReason]);

    const [comparisonData, setComparisonData] = useState(null);
    const [showComparisonModal, setShowComparisonModal] = useState(false);

    const fetchComparison = useCallback(async (v1Num, v2Num) => {
        if (!rfpId || !activeSection || !v1Num || !v2Num) return;
        setIsFetchingComparison(true);
        try {
            // Find section object to get its ID
            const sectionObj = editableData?.sections?.find(s => (s.label || s.key) === activeSection);
            const sectionId = sectionObj?.id || sectionObj?._id;

            if (sectionId) {
                const response = await api.get(`/rfp/${rfpId}/sections/${sectionId}/versions/compare`, {
                    params: { v1: v1Num, v2: v2Num }
                });
                setComparisonData(response.data);
                setShowComparisonModal(true);
            }
        } catch (error) {
            console.error('Error fetching comparison:', error);
        } finally {
            setIsFetchingComparison(false);
        }
    }, [rfpId, activeSection, editableData]);

    return {
        showVersionHistory,
        setShowVersionHistory,
        versionsList,
        activeVersionMenuId,
        setActiveVersionMenuId,
        isFetchingVersions,
        isFetchingVersionDetails,
        fetchVersions,
        handleViewVersion,

        // Comparison
        fetchComparison,
        comparisonData,
        showComparisonModal,
        setShowComparisonModal,

        // Modal states
        showLabelModal,
        setShowLabelModal,
        labelTargetVersion,
        setLabelTargetVersion,
        versionLabelText,
        setVersionLabelText,
        handleRenameVersion,

        showRevertModal,
        setShowRevertModal,
        revertTargetVersion,
        setRevertTargetVersion,
        revertReason,
        setRevertReason,
        handleRevertVersion,

        showVersionDetailsModal,
        setShowVersionDetailsModal,
        selectedVersionData,
        setSelectedVersionData,
        isSelectingCompareFromView,
        setIsSelectingCompareFromView,
        compareSourceVersion,
        setCompareSourceVersion,
        isFetchingComparison,
        setIsFetchingComparison
    };
};
